import React, { useState, useEffect } from 'react';
import { Clock, Save, Plus, X, Calendar, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { addBlockedDate, removeBlockedDate } from '../services/adminService';
import {
  getAvailabilitySettings,
  saveAvailabilitySettings,
  getBlockedDates,
} from '../services/availabilityService';
import { formatStoredBookingDateShort } from '../utils/bookingDates';
import { DEFAULT_GENERAL_SETTINGS } from '../utils/availabilitySettings';

const AvailabilityEditor = () => {
  const [availability, setAvailability] = useState({
    monday: { start: '06:00', end: '22:00', enabled: true },
    tuesday: { start: '06:00', end: '22:00', enabled: true },
    wednesday: { start: '06:00', end: '22:00', enabled: true },
    thursday: { start: '06:00', end: '22:00', enabled: true },
    friday: { start: '06:00', end: '22:00', enabled: true },
    saturday: { start: '06:00', end: '22:00', enabled: true },
    sunday: { start: '06:00', end: '22:00', enabled: true }
  });

  const [blockedDates, setBlockedDates] = useState([]);
  const [newBlockedDate, setNewBlockedDate] = useState({
    date: '',
    type: 'full',
    startTime: '06:00',
    endTime: '22:00',
    reason: ''
  });

  const [generalSettings, setGeneralSettings] = useState(DEFAULT_GENERAL_SETTINGS);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [settings, blocked] = await Promise.all([
        getAvailabilitySettings(),
        getBlockedDates(),
      ]);
      setAvailability(settings.weeklySchedule || availability);
      setGeneralSettings(settings.generalSettings || DEFAULT_GENERAL_SETTINGS);
      setBlockedDates(blocked);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load availability settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDayChange = (day, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const handleSaveAvailability = async () => {
    setSaving(true);
    try {
      await saveAvailabilitySettings(availability, generalSettings);
      const saved = await getAvailabilitySettings();
      setAvailability(saved.weeklySchedule || availability);
      setGeneralSettings(saved.generalSettings || DEFAULT_GENERAL_SETTINGS);
      toast.success(
        `Availability saved. Booking window: ${saved.generalSettings?.advanceBookingDays ?? generalSettings.advanceBookingDays} days.`
      );
    } catch (error) {
      console.error('Error saving availability:', error);
      toast.error('Failed to save availability settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddBlockedDate = async () => {
    if (!newBlockedDate.date) {
      toast.error('Please select a date');
      return;
    }

    try {
      const result = await addBlockedDate(
        newBlockedDate.date,
        newBlockedDate.type,
        newBlockedDate.type === 'partial' ? newBlockedDate.startTime : null,
        newBlockedDate.type === 'partial' ? newBlockedDate.endTime : null,
        newBlockedDate.reason
      );
      
      setBlockedDates(prev => [...prev, { ...newBlockedDate, id: result.blockedDateId }]);
      setNewBlockedDate({
        date: '',
        type: 'full',
        startTime: '09:00',
        endTime: '17:00',
        reason: ''
      });
      
      toast.success('Blocked date added successfully!');
    } catch (error) {
      console.error('Error adding blocked date:', error);
      toast.error('Failed to add blocked date');
    }
  };

  const handleRemoveBlockedDate = async (id) => {
    try {
      await removeBlockedDate(id);
      setBlockedDates(prev => prev.filter(date => date.id !== id));
      toast.success('Blocked date removed successfully!');
    } catch (error) {
      console.error('Error removing blocked date:', error);
      toast.error('Failed to remove blocked date');
    }
  };

  const days = [
    { key: 'monday', name: 'Monday' },
    { key: 'tuesday', name: 'Tuesday' },
    { key: 'wednesday', name: 'Wednesday' },
    { key: 'thursday', name: 'Thursday' },
    { key: 'friday', name: 'Friday' },
    { key: 'saturday', name: 'Saturday' },
    { key: 'sunday', name: 'Sunday' }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '16rem' }}>
        <div style={{ 
          width: '3rem', 
          height: '3rem', 
          border: '2px solid #C6A664', 
          borderTop: '2px solid transparent', 
          borderRadius: '50%', 
          animation: 'spin 1s linear infinite' 
        }}></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1rem' }}>
        <div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff', marginBottom: '0.5rem' }}>Availability Management</h2>
          <p style={{ color: '#d1d5db' }}>Configure weekly schedule and blocked dates</p>
        </div>
        <button
          onClick={handleSaveAvailability}
          disabled={saving}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#f59e0b',
            color: '#000000',
            border: 'none',
            borderRadius: '0.5rem',
            padding: '0.625rem 1rem',
            fontWeight: 700,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          <Save size={20} style={{ marginRight: '0.5rem' }} />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Weekly Schedule */}
      <div style={{ backgroundColor: '#111111', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '0.75rem', padding: '1rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
          <Clock size={20} style={{ marginRight: '0.5rem' }} />
          Weekly Schedule
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {days.map((day) => (
            <div key={day.key} style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(12.5rem, 100%), 1fr))', 
              gap: '1rem', 
              alignItems: 'center', 
              padding: '1rem', 
              backgroundColor: '#1a1a1a', 
              borderRadius: '0.5rem',
              border: '1px solid #374151'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={availability[day.key].enabled}
                  onChange={(e) => handleDayChange(day.key, 'enabled', e.target.checked)}
                  style={{ marginRight: '0.75rem', accentColor: '#C6A664' }}
                />
                <span style={{ color: '#ffffff', fontWeight: '500' }}>{day.name}</span>
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#d1d5db', marginBottom: '0.25rem' }}>Start Time</label>
                <input
                  type="time"
                  value={availability[day.key].start}
                  onChange={(e) => handleDayChange(day.key, 'start', e.target.value)}
                  disabled={!availability[day.key].enabled}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    border: '1px solid #4b5563',
                    borderRadius: '0.5rem',
                    padding: '0.55rem 0.65rem',
                    opacity: !availability[day.key].enabled ? 0.5 : 1,
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', color: '#d1d5db', marginBottom: '0.25rem' }}>End Time</label>
                <input
                  type="time"
                  value={availability[day.key].end}
                  onChange={(e) => handleDayChange(day.key, 'end', e.target.value)}
                  disabled={!availability[day.key].enabled}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    border: '1px solid #4b5563',
                    borderRadius: '0.5rem',
                    padding: '0.55rem 0.65rem',
                    opacity: !availability[day.key].enabled ? 0.5 : 1,
                  }}
                />
              </div>
              
              <div>
                <div style={{ fontSize: '0.875rem', color: '#d1d5db' }}>
                  {availability[day.key].enabled ? (
                    <span style={{ color: '#16a34a' }}>
                      Open {availability[day.key].start} - {availability[day.key].end}
                    </span>
                  ) : (
                    <span style={{ color: '#dc2626' }}>Closed</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* General Settings */}
      <div style={{ backgroundColor: '#111111', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '0.75rem', padding: '1rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff', marginBottom: '1rem' }}>General Settings</h3>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(15.625rem, 100%), 1fr))', gap: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#d1d5db', marginBottom: '0.5rem' }}>
              Advance Booking Days
            </label>
            <input
              type="number"
              min="1"
              max="365"
              value={generalSettings.advanceBookingDays}
              onChange={(e) => setGeneralSettings(prev => ({
                ...prev,
                advanceBookingDays: Number.parseInt(e.target.value, 10) || prev.advanceBookingDays,
              }))}
              style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#1a1a1a', color: '#fff', border: '1px solid #4b5563', borderRadius: '0.5rem', padding: '0.6rem 0.7rem' }}
            />
            <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginTop: '0.25rem' }}>
              How many days in advance customers can book
            </p>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#d1d5db', marginBottom: '0.5rem' }}>
              Block Days Before Booking
            </label>
            <input
              type="number"
              min="0"
              max="7"
              value={generalSettings.blockDaysBeforeBooking}
              onChange={(e) => setGeneralSettings(prev => ({
                ...prev,
                blockDaysBeforeBooking: Number.parseInt(e.target.value, 10) || prev.blockDaysBeforeBooking,
              }))}
              style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#1a1a1a', color: '#fff', border: '1px solid #4b5563', borderRadius: '0.5rem', padding: '0.6rem 0.7rem' }}
            />
            <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginTop: '0.25rem' }}>
              Days before booking that are blocked
            </p>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#d1d5db', marginBottom: '0.5rem' }}>
              Pre-Time Buffer (hours)
            </label>
            <input
              type="number"
              min="0"
              max="24"
              step="0.5"
              value={generalSettings.preTimeBuffer}
              onChange={(e) => setGeneralSettings(prev => ({
                ...prev,
                preTimeBuffer: parseFloat(e.target.value)
              }))}
              style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#1a1a1a', color: '#fff', border: '1px solid #4b5563', borderRadius: '0.5rem', padding: '0.6rem 0.7rem' }}
            />
            <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginTop: '0.25rem' }}>
              Hours blocked before each booking
            </p>
          </div>
          
          <div>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#d1d5db', marginBottom: '0.5rem' }}>
              Post-Time Buffer (hours)
            </label>
            <input
              type="number"
              min="0"
              max="24"
              step="0.5"
              value={generalSettings.postTimeBuffer}
              onChange={(e) => setGeneralSettings(prev => ({
                ...prev,
                postTimeBuffer: parseFloat(e.target.value)
              }))}
              style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#1a1a1a', color: '#fff', border: '1px solid #4b5563', borderRadius: '0.5rem', padding: '0.6rem 0.7rem' }}
            />
            <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginTop: '0.25rem' }}>
              Hours blocked after each booking
            </p>
          </div>
        </div>
      </div>

      {/* Blocked Dates */}
      <div style={{ backgroundColor: '#111111', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '0.75rem', padding: '1rem' }}>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#ffffff', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
          <Calendar size={20} style={{ marginRight: '0.5rem' }} />
          Blocked Dates
        </h3>
        
        {/* Add New Blocked Date */}
        <div style={{ backgroundColor: '#1a1a1a', borderRadius: '0.5rem', padding: '1rem', marginBottom: '1.5rem', border: '1px solid #374151' }}>
          <h4 style={{ fontSize: '1rem', fontWeight: '500', color: '#ffffff', marginBottom: '1rem' }}>Add Blocked Date</h4>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.5rem' }}>
                Date
              </label>
              <input
                type="date"
                value={newBlockedDate.date}
                onChange={(e) => setNewBlockedDate(prev => ({
                  ...prev,
                  date: e.target.value
                }))}
                style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#000', color: '#fff', border: '1px solid #4b5563', borderRadius: '0.5rem', padding: '0.6rem 0.7rem' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.5rem' }}>
                Type
              </label>
              <select
                value={newBlockedDate.type}
                onChange={(e) => setNewBlockedDate(prev => ({
                  ...prev,
                  type: e.target.value
                }))}
                style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#000', color: '#fff', border: '1px solid #4b5563', borderRadius: '0.5rem', padding: '0.6rem 0.7rem' }}
              >
                <option value="full">Full Day</option>
                <option value="partial">Partial Day</option>
              </select>
            </div>
            
            {newBlockedDate.type === 'partial' && (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.5rem' }}>
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={newBlockedDate.startTime}
                    onChange={(e) => setNewBlockedDate(prev => ({
                      ...prev,
                      startTime: e.target.value
                    }))}
                    style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#000', color: '#fff', border: '1px solid #4b5563', borderRadius: '0.5rem', padding: '0.6rem 0.7rem' }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.5rem' }}>
                    End Time
                  </label>
                  <input
                    type="time"
                    value={newBlockedDate.endTime}
                    onChange={(e) => setNewBlockedDate(prev => ({
                      ...prev,
                      endTime: e.target.value
                    }))}
                    style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#000', color: '#fff', border: '1px solid #4b5563', borderRadius: '0.5rem', padding: '0.6rem 0.7rem' }}
                  />
                </div>
              </>
            )}
          </div>
          
          <div style={{ marginTop: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#6b7280', marginBottom: '0.5rem' }}>
              Reason (Optional)
            </label>
            <select
              value={newBlockedDate.reason}
              onChange={(e) => setNewBlockedDate(prev => ({
                ...prev,
                reason: e.target.value
              }))}
              style={{ width: '100%', boxSizing: 'border-box', backgroundColor: '#000', color: '#fff', border: '1px solid #4b5563', borderRadius: '0.5rem', padding: '0.6rem 0.7rem' }}
            >
              <option value="">Select reason</option>
              <option value="Maintenance">Maintenance</option>
              <option value="Event">Event</option>
              <option value="PTO">PTO</option>
            </select>
          </div>
          
          <button
            onClick={handleAddBlockedDate}
            disabled={!newBlockedDate.date}
            style={{ marginTop: '1rem', display: 'inline-flex', alignItems: 'center', backgroundColor: '#f59e0b', color: '#000', border: 'none', borderRadius: '0.5rem', padding: '0.6rem 1rem', fontWeight: 700, cursor: !newBlockedDate.date ? 'not-allowed' : 'pointer', opacity: !newBlockedDate.date ? 0.6 : 1 }}
          >
            <Plus size={16} style={{ marginRight: '0.5rem' }} />
            Add Blocked Date
          </button>
        </div>
        
        {/* Existing Blocked Dates */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {blockedDates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
              <Calendar size={48} style={{ margin: '0 auto', marginBottom: '1rem', opacity: 0.5 }} />
              <p>No blocked dates configured</p>
            </div>
          ) : (
            blockedDates.map((blockedDate) => (
              <div key={blockedDate.id} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                padding: '1rem', 
                backgroundColor: '#1a1a1a', 
                borderRadius: '0.5rem',
                border: '1px solid #374151'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Calendar size={16} style={{ color: '#f59e0b' }} />
                    <span style={{ color: '#ffffff', fontWeight: '500' }}>
                      {formatStoredBookingDateShort(blockedDate.date)}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <AlertCircle size={16} style={{ color: '#f59e0b' }} />
                    <span style={{ color: '#d1d5db' }}>
                      {blockedDate.type === 'full' ? 'Full Day' : `${blockedDate.startTime} - ${blockedDate.endTime}`}
                    </span>
                  </div>
                  
                  {blockedDate.reason && (
                    <span style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
                      ({blockedDate.reason})
                    </span>
                  )}
                </div>
                
                <button
                  onClick={() => handleRemoveBlockedDate(blockedDate.id)}
                  style={{ 
                    padding: '0.5rem', 
                    color: '#dc2626', 
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = '#b91c1c';
                    e.target.style.backgroundColor = '#fef2f2';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = '#dc2626';
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AvailabilityEditor;
